import { cn } from '@quadralize/utils'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import * as React from 'react'
import { useIntersectionTrigger } from '../../../hooks/use-intersection-trigger'
import { Skeleton } from '../../feedback/skeleton/skeleton'
import { AnimateIn, StaggerChildren } from '../../utils/motion-components'
import { DataTablePagination } from './data-table-pagination'
import { DataTableViewOptions } from './data-table-view-options'

// Default column size from TanStack Table
const DEFAULT_COLUMN_SIZE = 150

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
  manualPagination?: boolean
  pageCount?: number
  rowCount?: number
  onPaginationChange?: OnChangeFn<PaginationState>
  pagination?: PaginationState
  isLoading?: boolean
  onEndReached?: () => void
  isLoadingMore?: boolean
  showViewOptions?: boolean
  onRowClick?: (row: TData) => void
}

function shouldIgnoreRowClick(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  const interactiveElement = target.closest(
    'button, a, input, textarea, select, summary, [role="button"], [role="menuitem"], [data-row-click-ignore="true"]',
  )

  return !!interactiveElement
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  manualPagination = false,
  pageCount,
  rowCount,
  onPaginationChange,
  pagination,
  isLoading = false,
  onEndReached,
  isLoadingMore = false,
  showViewOptions = false,
  onRowClick,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const observerTarget = React.useRef<HTMLDivElement>(null)

  // Infer if pagination should be enabled based on props
  const enablePagination = manualPagination || !!pageCount || !!onPaginationChange

  const table = useReactTable({
    data,
    columns,
    pageCount,
    rowCount,
    manualPagination,
    enableHiding: showViewOptions,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: onPaginationChange,
    getPaginationRowModel: enablePagination && !onEndReached ? getPaginationRowModel() : undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(pagination && { pagination }),
    },
  })

  useIntersectionTrigger({
    target: observerTarget,
    onIntersect: onEndReached,
    enabled: !!onEndReached,
    canTrigger: !isLoadingMore,
    threshold: 0.1,
  })

  return (
    <div className={cn('flex flex-col gap-3 md:h-full md:min-h-0 md:flex-1', className)}>
      {showViewOptions && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto ml-auto">
            <DataTableViewOptions table={table} />
          </div>
        </div>
      )}
      <div className="rounded-md border border-border-subtle bg-bg-elevated overflow-hidden md:flex md:min-h-0 md:flex-1 md:flex-col">
        <div
          className="w-full overflow-auto max-h-150 outline-none md:flex-1 md:h-full md:max-h-none"
          tabIndex={-1}
        >
          <table className="w-full caption-bottom text-sm text-left outline-none">
            <thead className="[&_tr]:border-b [&_tr]:border-border-subtle sticky top-0 bg-bg-elevated z-10 shadow-sm">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b transition-colors hover:bg-bg-subtle/50 data-[state=selected]:bg-bg-subtle"
                >
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        className="h-10 px-3 text-left align-middle font-medium text-text-secondary first:pl-6 last:pr-6 outline-none [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5"
                        style={{
                          width:
                            header.getSize() !== DEFAULT_COLUMN_SIZE ? header.getSize() : undefined,
                          maxWidth: header.column.columnDef.maxSize,
                          minWidth: header.column.columnDef.minSize,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <StaggerChildren as="tbody" className="[&_tr:last-child]:border-0" key={data.length}>
              {isLoading
                ? Array.from({ length: table.getState().pagination?.pageSize ?? 10 }).map(
                    (_, i) => (
                      <tr
                        key={`loading-row-${i.toString()}`}
                        className="border-b border-border-subtle last:border-0 hover:bg-transparent"
                      >
                        {table.getVisibleFlatColumns().map((column) => (
                          <td key={column.id} className="p-2">
                            <Skeleton className="h-6 w-full" />
                          </td>
                        ))}
                      </tr>
                    ),
                  )
                : table.getRowModel().rows?.length
                  ? table.getRowModel().rows.map((row) => (
                      <AnimateIn
                        as="tr"
                        key={row.id}
                        variant="fadeIn"
                        data-state={row.getIsSelected() && 'selected'}
                        onClick={(event) => {
                          if (!onRowClick || shouldIgnoreRowClick(event.target)) {
                            return
                          }

                          onRowClick(row.original)
                        }}
                        className={cn(
                          'border-b border-border-subtle transition-colors hover:bg-bg-subtle/50 data-[state=selected]:bg-bg-subtle',
                          onRowClick && 'cursor-pointer',
                        )}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="p-3 align-middle first:pl-6 last:pr-6 [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5"
                            style={{
                              width:
                                cell.column.getSize() !== DEFAULT_COLUMN_SIZE
                                  ? cell.column.getSize()
                                  : undefined,
                              maxWidth: cell.column.columnDef.maxSize,
                              minWidth: cell.column.columnDef.minSize,
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </AnimateIn>
                    ))
                  : !isLoadingMore && (
                      <tr className="border-0">
                        <td
                          colSpan={columns.length}
                          className="h-24 text-center text-text-secondary"
                        >
                          Nenhum resultado encontrado.
                        </td>
                      </tr>
                    )}
              {isLoadingMore &&
                Array.from({ length: 3 }).map((_, i) => (
                  <tr
                    key={`loading-more-${i.toString()}`}
                    className="border-b border-border-subtle last:border-0 hover:bg-transparent"
                  >
                    {table.getVisibleFlatColumns().map((column) => (
                      <td key={column.id} className="p-2">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
            </StaggerChildren>
          </table>
          {onEndReached && <div ref={observerTarget} className="h-4 w-full" />}
        </div>
      </div>
      {enablePagination && !onEndReached && <DataTablePagination table={table} />}
    </div>
  )
}
